import {
  OptimizedGantt,
  project,
  ProjectElementType,
  ResourceTypes,
  WorkLoadsCalendar,
} from "@planzone/dto";
import { DateTime } from "luxon";
import React from "react";
import { useProjectResourcesRetrieval } from "@planzone/view-models";
import { useWorkspaceContext } from "@scenes/workspaceContext";
import styles from "./styles.scss";

function getDates(minDateIso: string, maxDateIso: string): Date[] {
  // Returns dates from (minDate + 1 day) through (maxDate + 1 day) at
  // local-midnight granularity. The +1 offset is intentional and is
  // compensated by the parent's ±1 month pre-shift in index.tsx — see
  // audit §11. Do NOT collapse this off-by-one without also updating the
  // parent compensation; the two are a single contract.
  //
  // The pre-migration implementation mixed a millisecond-tick loop bound
  // with calendar-day construction, which produced subtly different
  // results across DST transitions (audit §12 Q4). The Luxon version
  // uses calendar-day arithmetic consistently — same output under all
  // non-DST conditions, and well-defined under DST.
  const min = DateTime.fromISO(minDateIso);
  const max = DateTime.fromISO(maxDateIso);
  if (!min.isValid || !max.isValid) return [];

  const dates: Date[] = [];
  const limit = max.plus({ days: 1 }).startOf("day");
  let cursor = min.plus({ days: 1 }).startOf("day");
  while (cursor <= limit) {
    dates.push(cursor.toJSDate());
    cursor = cursor.plus({ days: 1 });
  }
  return dates;
}

function reCalculateNodeDateRange(
  projectElement: OptimizedGantt[number],
  minDate: DateTime,
  maxDate: DateTime,
): { startDate: DateTime; endDate: DateTime } {
  return {
    startDate: projectElement.startDate
      ? DateTime.fromISO(projectElement.startDate, { zone: "utc" })
      : minDate,
    endDate: projectElement.endDate
      ? DateTime.fromISO(projectElement.endDate, { zone: "utc" })
      : maxDate,
  };
}

function reCalculateLeafDateRange(
  projectElement: OptimizedGantt[number],
  minDate: DateTime,
  maxDate: DateTime,
): { startDate: DateTime; endDate: DateTime } {
  const endDate = projectElement.endDate
    ? DateTime.fromISO(projectElement.endDate, { zone: "utc" })
    : maxDate;

  return {
    startDate: projectElement.startDate
      ? DateTime.fromISO(projectElement.startDate, { zone: "utc" })
      : projectElement.endDate
        ? // FIXME: timezone bug — same shape as WBS utils.ts:184 — separate ticket.
          // The derived startDate uses local-TZ day of the source endDate,
          // producing different output across browsers. Preserved here to
          // match the audit; cleanup deferred. `.toLocal()` matches the
          // pre-migration `DateTime.fromJSDate(endDate)` step which
          // interpreted the UTC Date in browser-local TZ before snapping.
          endDate.toLocal().startOf("day")
        : maxDate.toLocal().startOf("day"),
    endDate,
  };
}

function reCalculateDateRange(
  projectElement: OptimizedGantt[number],
  minDate: DateTime,
  maxDate: DateTime,
): { startDate: DateTime; endDate: DateTime } {
  return projectElement.type === ProjectElementType.LIST ||
    projectElement.type === ProjectElementType.PROJECT
    ? reCalculateNodeDateRange(projectElement, minDate, maxDate)
    : reCalculateLeafDateRange(projectElement, minDate, maxDate);
}

function renderWithTreeCtx(
  gantt: OptimizedGantt,
  isOpen: (id: string) => boolean,
  render: (
    projectElement: OptimizedGantt[number],
    ctx: { minDate: string; maxDate: string },
    index: number,
    fullCtx: {
      [key: number]: { minDate: string; maxDate: string };
    },
  ) => React.JSX.Element,
): React.ReactNode[] {
  return gantt.reduce<{
    ui: React.JSX.Element[];
    filteredCount: number;
    ctx: {
      [key: number]: {
        minDate: string;
        maxDate: string;
        filtered: boolean;
      };
    };
  }>(
    ({ ui, ctx, filteredCount }, projectElement, index) => {
      const isChangingContext =
        projectElement.type === ProjectElementType.PROJECT;
      const isChangingDepth =
        projectElement.type === ProjectElementType.LIST &&
        gantt[index + 1]?.depth !== projectElement.depth;
      const filtered = ctx[projectElement.depth - 1]?.filtered;

      return {
        filteredCount: (
          isChangingContext
            ? !!projectElement.startDate && !!projectElement.endDate
            : !filtered
        )
          ? filteredCount
          : filteredCount + 1,
        ui: (
          isChangingContext
            ? !!projectElement.startDate && !!projectElement.endDate
            : !filtered
        )
          ? [
              ...ui,
              render(
                projectElement,
                ctx[
                  projectElement.depth - 1 > 0 ? projectElement.depth - 1 : 0
                ],
                index - filteredCount,
                ctx,
              ),
            ]
          : ui,
        ctx: isChangingContext
          ? {
              0: {
                minDate: projectElement.startDate,
                maxDate: projectElement.endDate,
                filtered:
                  !projectElement.startDate ||
                  !projectElement.endDate ||
                  !isOpen(projectElement.uuid),
              },
            }
          : isChangingDepth
            ? {
                ...ctx,
                [projectElement.depth]: {
                  filtered: filtered || !isOpen(projectElement.uuid),
                  minDate:
                    projectElement.startDate ||
                    ctx[projectElement.depth]?.minDate ||
                    ctx[projectElement.depth - 1]?.minDate ||
                    "",
                  maxDate:
                    projectElement.endDate ||
                    ctx[projectElement.depth]?.maxDate ||
                    ctx[projectElement.depth - 1]?.maxDate ||
                    "",
                },
              }
            : ctx,
      };
    },
    {
      filteredCount: 0,
      ui: [],
      ctx: {
        0: {
          minDate: gantt[0]?.startDate,
          maxDate: gantt[0]?.endDate,
          filtered: !gantt[0]?.startDate || !gantt[0]?.endDate,
        },
      },
    },
  ).ui;
}

// Inside a project, the workloads of its members. Elsewhere no resource is given: the back computes the
// workloads of every planzone resource, so the whole list is not loaded for its uuids.
function useResources() {
  const ctx = useWorkspaceContext();
  const isInProject = ctx.type === "project";
  const { projectResources } = useProjectResourcesRetrieval(
    isInProject ? ctx.project : { ...project, uuid: "" },
  );

  return {
    resources: isInProject
      ? projectResources.map(({ resource }) => resource)
      : [],
  };
}

function isToday(dt: DateTime) {
  return dt.hasSame(DateTime.now(), "day");
}

// Duplicated from elementsOverlay/element/utils/drag.ts (not exported there).
// Worth hoisting both copies to a shared util in a later pass.
//
// The old `!!mouseEvent.clientX` check misclassified a genuine mouse event
// at clientX === 0 (left viewport edge) as touch, then read
// `changedTouches` off a MouseEvent → TypeError. `in` narrowing is exact.
function getClientX(e: MouseEvent | TouchEvent): number {
  if ("changedTouches" in e) {
    return e.changedTouches.length > 0 ? e.changedTouches[0].clientX : 0;
  }
  return e.clientX;
}

// Drop-in replacement — the returned API ({ rangeRef, selectThumb }) is
// unchanged, so PanelResizer call sites need no edits. `edit` may keep
// being an inline setter at the call site: handlers read it through a
// latest-ref, so its identity doesn't matter.
//
// Before: 4 permanent window listeners per instance while mounted,
// re-subscribed on EVERY render of the owning scene (dep-less effect →
// 8 listener ops per render), an unconditional getBoundingClientRect +
// getComputedStyle on every mousemove anywhere — the hasSelection() gate
// came AFTER the reads — and 2 scene-wide renders per drag from
// setCanMove. Unmounting mid-drag could leave the `unselectable` class
// stuck on <html>.
//
// After: zero window listeners at rest; exactly 4 during an active drag,
// detached on release or unmount (class removed either way); one rect
// read per move, on the tracked element only; zero listener churn on
// renders; zero renders from the hook itself.
function useRange(offset: number, edit: (data: number) => void) {
  const rangeRef = React.useRef<HTMLDivElement>(null);

  // A drag outlives renders of the owner; handlers must call the LATEST
  // edit. Reading through a ref removes any dependency on the callback's
  // identity.
  const editRef = React.useRef(edit);
  React.useLayoutEffect(() => {
    editRef.current = edit;
  });

  // Replaces the old `canMove` React state: it was only ever read inside
  // event handlers, never rendered. Non-null ⇔ a drag is in progress, and
  // it doubles as the detach function.
  const teardownRef = React.useRef<(() => void) | null>(null);

  const selectThumb = () => {
    if (teardownRef.current) return; // already dragging

    // Applied at press start rather than lazily on first move (the old
    // classList.contains check per move is gone with it).
    document.documentElement.classList.add(styles.unselectable);

    const dispatchChange = (e: MouseEvent | TouchEvent) => {
      const rangeElement = rangeRef.current;
      if (!rangeElement) return;

      // One layout read per move, on this element only. rect.width
      // replaces the old getComputedStyle(...).width — same value for a
      // border-box track, one forced read instead of two, and correct
      // under CSS transforms.
      const rect = rangeElement.getBoundingClientRect();
      if (rect.width <= 0) return;

      const nextValue = Math.round(getClientX(e) - rect.left);
      const percent = Math.round((nextValue / rect.width) * 100);

      percent >= 0 && percent <= 100 && editRef.current(percent);
    };

    const teardown = () => {
      window.removeEventListener("touchmove", dispatchChange);
      window.removeEventListener("mousemove", dispatchChange);
      window.removeEventListener("mouseup", cancelSelection);
      window.removeEventListener("touchend", cancelSelection);
      document.documentElement.classList.remove(styles.unselectable);
      teardownRef.current = null;
    };

    const cancelSelection = () => teardown();

    window.addEventListener("touchmove", dispatchChange);
    window.addEventListener("mousemove", dispatchChange);
    window.addEventListener("mouseup", cancelSelection);
    window.addEventListener("touchend", cancelSelection);
    teardownRef.current = teardown;
  };

  // If the owner unmounts mid-drag, detach the window listeners it owned
  // and clear the selection guard class.
  React.useEffect(() => {
    return () => {
      if (teardownRef.current) teardownRef.current();
    };
  }, []);

  return { rangeRef, selectThumb };
}

function sortWorkLoads(workLoads: WorkLoadsCalendar): WorkLoadsCalendar {
  return [...workLoads]
    .sort((a, b) => a.resourceName.localeCompare(b.resourceName))
    .sort((a, b) => {
      const priority: Record<string, number> = {
        [ResourceTypes.HUMAN]: 1,
        [ResourceTypes.VIRTUAL]: 2,
        [ResourceTypes.MATERIAL]: 3,
      };

      return (priority[a.resourceType] ?? 4) - (priority[b.resourceType] ?? 4);
    });
}

export {
  getDates,
  reCalculateDateRange,
  renderWithTreeCtx,
  useResources,
  isToday,
  useRange,
  sortWorkLoads,
};
